// src/Pages/Announcements/announcementFormat.jsx
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

// FLIP TO true WHEN PHASE B SHIPS (announcements → drafts). Until then the
// UI must not promise posts that will not arrive.
export const GENERATION_LIVE = true;

export const PENDING = ['received', 'interpreting'];

export const KIND_LABELS = {
  operational_temporary: 'Change to hours',
  fact_change:           'Business update',
  news:                  'News',
  promotion:             'Offer',
  event:                 'Event',
  story:                 'Story',
  instruction:           'Request to Revfer',
  unsupported:           'Not something we can post',
};

export const STATUS_LABELS = {
  received:      { label: 'Reading…',          color: 'default' },
  interpreting:  { label: 'Reading…',          color: 'default' },
  interpreted:   { label: 'Understood',        color: 'success' },
  nothing_found: { label: 'Nothing to post',   color: 'default' },
  unsupported:   { label: "Can't post this",   color: 'warning' },
  failed:        { label: "Couldn't read",     color: 'error' },
  discarded:     { label: 'Discarded',         color: 'default' },
};

// Dates are the business's local calendar dates (YYYY-MM-DD). Formatted at
// noon UTC in UTC so the viewer's own time zone can never shift the day.
export const fmtDay = (date) =>
  new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`));

export const fmtTime = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};

const RECURS_TEXT = { daily: 'every day', weekdays: 'every weekday', weekends: 'every weekend' };

export function describeWindow(w) {
  if (!w) return null;
  const parts = [];
  if (w.startDate && w.endDate && w.endDate !== w.startDate) parts.push(`${fmtDay(w.startDate)} – ${fmtDay(w.endDate)}`);
  else if (w.startDate) parts.push(fmtDay(w.startDate));
  else if (w.endDate)   parts.push(`until ${fmtDay(w.endDate)}`);

  if (w.startTime && w.endTime) parts.push(`${fmtTime(w.startTime)} – ${fmtTime(w.endTime)}`);
  else if (w.startTime)         parts.push(`from ${fmtTime(w.startTime)}`);
  else if (w.endTime)           parts.push(`until ${fmtTime(w.endTime)}`);

  if (w.recurs) parts.push(Array.isArray(w.recurs) ? `every ${w.recurs.join(', ')}` : RECURS_TEXT[w.recurs]);
  return parts.length ? parts.join(' · ') : null;
}

// The interpreter's best guesses, said plainly. Only the ones an owner can act on.
function flagNote(f, w) {
  switch (f.reason) {
    case 'next_weekday_ambiguous':
      return f.alternative ? `We assumed ${fmtDay(w.startDate)}. If you meant ${fmtDay(f.alternative)}, send a quick correction.` : null;
    case 'start_assumed_today':   return 'We assumed it starts today.';
    case 'month_end_applied':     return 'Runs to the end of the month.';
    case 'offer_missing':         return "We didn't find the offer itself. Mention the deal so we can include it.";
    default:                      return null;
  }
}

export function AnnouncementItems({ items, dense = false }) {
  if (!items?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.75 : 1.25 }}>
      {items.map((it, i) => {
        const when  = describeWindow(it.window);
        const notes = (it.flags || []).map(f => flagNote(f, it.window)).filter(Boolean);
        return (
          <Box key={i}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={KIND_LABELS[it.kind] || it.kind} size="small" variant="outlined"
                    color={it.kind === 'unsupported' ? 'warning' : 'primary'} sx={{ height: 20, fontSize: '0.7rem' }} />
              {when && <Typography variant="caption" fontWeight={600}>{when}</Typography>}
            </Box>
            {!dense && it.summary && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{it.summary}</Typography>
            )}
            {notes.map((n, k) => (
              <Typography key={k} variant="caption" color="warning.dark" sx={{ display: 'block' }}>{n}</Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}